import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth, wsUrl } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Loader2, Send, Sparkles, Wine, Crown, Clock, Users } from "lucide-react";
import { toast } from "sonner";

export default function MultiplayerRoom() {
  const { mode, roomId } = useParams();
  const { lang } = useT();
  const navigate = useNavigate();
  const me = auth.getUser();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [phase, setPhase] = useState("connecting"); // connecting|waiting|starting|playing|ended
  const [players, setPlayers] = useState([]);
  const [question, setQuestion] = useState(null);
  const [round, setRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);
  const [answerIdx, setAnswerIdx] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [scores, setScores] = useState({});
  const [winner, setWinner] = useState(null);
  // blind
  const [blindDescription, setBlindDescription] = useState("");
  const [blindHints, setBlindHints] = useState([]);
  const [blindGuess, setBlindGuess] = useState({ color: "", grape: "", region: "" });
  const [blindReveal, setBlindReveal] = useState(null);
  // speed
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [hbMessage, setHbMessage] = useState(null);
  const [speedPrompt, setSpeedPrompt] = useState(null);
  const [speedRemaining, setSpeedRemaining] = useState(null);

  useEffect(() => {
    if (!auth.getToken()) {
      navigate("/login");
      return;
    }
    const url = `${wsUrl(`/multiplayer/ws/${roomId}`)}?token=${encodeURIComponent(auth.getToken())}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      setPhase("waiting");
    };
    ws.onclose = (e) => {
      setConnected(false);
      if (e.code === 4403) toast.error(lang === "fr" ? "Salle pleine" : "Room is full");
      else if (e.code === 4401) toast.error(lang === "fr" ? "Session expirée" : "Session expired");
      else if (e.code === 4404) toast.error(lang === "fr" ? "Salle introuvable" : "Room not found");
    };
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      switch (msg.type) {
        case "room_state":
          setPlayers(msg.players || []);
          if (msg.phase) setPhase(msg.phase);
          break;
        case "starting":
          setPhase("starting");
          break;
        case "question":
          setPhase("playing");
          setQuestion({ q: msg.q, choices: msg.choices });
          setRound(msg.round);
          setTotalRounds(msg.total);
          setAnswerIdx(null);
          setLastResult(null);
          break;
        case "round_result":
          setLastResult(msg);
          setScores(msg.scores || {});
          setPlayers(msg.players || []);
          break;
        case "game_over":
          setPhase("ended");
          setWinner({ id: msg.winner_id, name: msg.winner_name });
          setScores(msg.scores || {});
          setPlayers(msg.players || []);
          break;
        case "blind_start":
          setPhase("playing");
          setBlindDescription(msg.description || "");
          setBlindHints(msg.hints || []);
          break;
        case "blind_reveal":
          setPhase("ended");
          setBlindReveal({ answer: msg.answer, scores: msg.scores, guesses: msg.guesses });
          setScores(msg.scores || {});
          break;
        case "speed_start":
          setPhase("playing");
          setSpeedRemaining(msg.duration_s);
          break;
        case "speed_prompt":
          setSpeedPrompt(msg.prompt);
          setSpeedRemaining(msg.remaining_s);
          break;
        case "speed_end":
          setPhase("ended");
          break;
        case "chat":
          setChat((c) => [...c, msg]);
          break;
        case "heartbeat":
          // keep-alive; surface optional status text
          if (msg.message) setHbMessage(msg.message);
          break;
        case "error":
          toast.error(msg.message || "Erreur");
          break;
        default:
          break;
      }
    };
    return () => { try { ws.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Speed timer countdown locally too
  useEffect(() => {
    if (mode !== "speed" || speedRemaining == null || phase !== "playing") return;
    const t = setInterval(() => setSpeedRemaining((r) => (r != null && r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [mode, phase, speedRemaining]);

  const send = (obj) => { try { wsRef.current?.send(JSON.stringify(obj)); } catch {} };
  const sendReady = () => send({ type: "ready" });
  const sendAnswer = (i) => { setAnswerIdx(i); send({ type: "answer", round, choice_index: i }); };
  const sendGuess = () => send({ type: "guess", ...blindGuess });
  const sendChat = (e) => {
    e?.preventDefault();
    if (!chatInput.trim()) return;
    send({ type: "chat", text: chatInput.trim() });
    setChat((c) => [...c, { from: me?.id, from_name: lang === "fr" ? "Moi" : "Me", text: chatInput.trim(), local: true }]);
    setChatInput("");
  };

  const meReady = players.find((p) => p.user_id === me?.id)?.ready;
  const opponent = players.find((p) => p.user_id !== me?.id);

  // ---------- Render ----------
  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-14" data-testid={`mp-room-${mode}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <span className="chip-pink uppercase tracking-wider">{mode}</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F] mt-2">
            {mode === "battle" && (lang === "fr" ? "Battle 1v1" : "Battle 1v1")}
            {mode === "blind" && (lang === "fr" ? "Dégustation à l'aveugle" : "Blind tasting")}
            {mode === "speed" && (lang === "fr" ? "Speed-meeting B2B" : "B2B speed-meeting")}
          </h1>
        </div>
        <div className="text-xs text-[#8B95A1]">
          {connected ? <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500" /> {lang === "fr" ? "Connecté" : "Connected"}</span> : <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> {lang === "fr" ? "Connexion…" : "Connecting…"}</span>}
        </div>
      </div>

      {/* Players panel */}
      <div className="mt-6 rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 flex items-center gap-4 flex-wrap" data-testid="mp-players">
        <Users className="w-5 h-5 text-[#E5276A]" />
        {players.length === 0 && <span className="text-sm text-[#8B95A1]">{lang === "fr" ? "Aucun joueur" : "No players"}</span>}
        {players.map((p) => (
          <div key={p.user_id} className="flex items-center gap-2 text-sm">
            <span className={`inline-block w-2 h-2 rounded-full ${p.ready ? "bg-green-500" : "bg-amber-400"}`} />
            <span className={p.user_id === me?.id ? "font-bold text-[#1F3A5F]" : "text-[#1F3A5F]"}>{p.name}{p.user_id === me?.id ? " (vous)" : ""}</span>
            {mode === "battle" && <span className="text-[#E5276A] font-bold">· {p.score || 0}</span>}
          </div>
        ))}
        {players.length < 2 && (
          <span className="ml-auto text-xs text-[#8B95A1] inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {lang === "fr" ? "En attente d'un adversaire…" : "Waiting for an opponent…"}</span>
        )}
      </div>

      {/* Waiting / Ready */}
      {phase === "waiting" && players.length >= 2 && !meReady && (
        <div className="mt-6 rounded-2xl border border-[#E5276A]/30 bg-[#FCE4ED] p-6 text-center">
          <p className="text-[#1F3A5F]">{lang === "fr" ? "Adversaire trouvé ! Prêt à commencer ?" : "Opponent found! Ready?"}</p>
          <button data-testid="mp-ready" onClick={sendReady} className="mt-3 px-5 py-2 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
            {lang === "fr" ? "Je suis prêt" : "I'm ready"}
          </button>
        </div>
      )}
      {phase === "waiting" && meReady && players.length >= 2 && (
        <div className="mt-6 rounded-xl bg-[#FBF7F3] p-4 text-sm text-[#4A5568] text-center">
          {lang === "fr" ? "En attente de l'autre joueur…" : "Waiting for the other player…"}
        </div>
      )}
      {phase === "starting" && (
        <div className="mt-6 text-center py-8">
          <Sparkles className="w-8 h-8 text-[#E5276A] mx-auto animate-pulse" />
          <p className="mt-3 font-display text-xl text-[#1F3A5F]">{hbMessage || (lang === "fr" ? "geoVINUM AI prépare la partie…" : "geoVINUM AI is loading the match…")}</p>
        </div>
      )}

      {/* BATTLE mode */}
      {mode === "battle" && phase === "playing" && question && (
        <div className="mt-6 rounded-2xl border border-[#1F3A5F]/12 bg-white p-6" data-testid="mp-battle-question">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-[#E5276A]">{lang === "fr" ? "Question" : "Question"} {round}/{totalRounds}</span>
          </div>
          <h2 className="mt-2 font-display text-xl md:text-2xl text-[#1F3A5F]">{question.q}</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {question.choices.map((c, i) => (
              <button
                key={i}
                data-testid={`mp-battle-choice-${i}`}
                disabled={answerIdx !== null}
                onClick={() => sendAnswer(i)}
                className={`text-left px-4 py-3 rounded-xl border transition-colors ${
                  answerIdx === i
                    ? "border-[#E5276A] bg-[#FCE4ED] text-[#1F3A5F]"
                    : "border-[#1F3A5F]/15 bg-white text-[#1F3A5F] hover:border-[#E5276A] hover:bg-[#FBF7F3]"
                } disabled:opacity-70`}
              >
                <span className="font-bold mr-2 text-[#E5276A]">{String.fromCharCode(65 + i)}.</span>
                {c}
              </button>
            ))}
          </div>
          {lastResult && lastResult.round === round && (
            <div className="mt-4 rounded-xl bg-[#FBF7F3] p-3 text-sm text-[#4A5568]">
              <span className="font-bold text-[#1F3A5F]">{lang === "fr" ? "Bonne réponse :" : "Correct:"}</span> {question.choices[lastResult.correct_index]}. {lastResult.explain}
            </div>
          )}
        </div>
      )}

      {/* BLIND mode */}
      {mode === "blind" && phase === "playing" && (
        <div className="mt-6 space-y-5">
          <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-6">
            <div className="flex items-baseline gap-2"><Wine className="w-4 h-4 text-[#E5276A]" /><span className="text-xs font-bold uppercase tracking-wider text-[#E5276A]">{lang === "fr" ? "Le vin mystère" : "The mystery wine"}</span></div>
            <p className="mt-2 text-[#1F3A5F] leading-relaxed">{blindDescription}</p>
            {blindHints.length > 0 && (
              <ul className="mt-3 text-xs text-[#4A5568] space-y-0.5">
                {blindHints.map((h, i) => <li key={i}>· {h}</li>)}
              </ul>
            )}
          </div>
          <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-6 space-y-3" data-testid="mp-blind-guess">
            {["color", "grape", "region"].map((k) => (
              <div key={k}>
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{k}</label>
                <input
                  data-testid={`mp-blind-${k}`}
                  value={blindGuess[k]}
                  onChange={(e) => setBlindGuess((g) => ({ ...g, [k]: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
                />
              </div>
            ))}
            <button
              data-testid="mp-blind-submit"
              onClick={sendGuess}
              className="px-5 py-2 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]"
            >
              {lang === "fr" ? "Soumettre" : "Submit"}
            </button>
          </div>
        </div>
      )}

      {/* SPEED mode */}
      {mode === "speed" && phase === "playing" && (
        <div className="mt-6 space-y-5">
          <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-[#E5276A]">{lang === "fr" ? "Prompt actuel" : "Current prompt"}</span>
              {speedRemaining != null && (
                <span className="text-xs font-bold text-[#1F3A5F] inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {Math.max(0, Math.floor(speedRemaining))}s
                </span>
              )}
            </div>
            <p className="mt-2 font-display text-lg text-[#1F3A5F]">{speedPrompt || (lang === "fr" ? "Présentez-vous !" : "Introduce yourself!")}</p>
          </div>
          <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-4 h-72 overflow-y-auto space-y-2" data-testid="mp-speed-chat">
            {chat.length === 0 && <p className="text-sm text-[#8B95A1] italic">{lang === "fr" ? "Lancez la conversation…" : "Start the conversation…"}</p>}
            {chat.map((m, i) => (
              <div key={i} className={`flex ${m.from === me?.id ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.from === me?.id ? "bg-[#E5276A] text-white" : "bg-[#FBF7F3] text-[#1F3A5F]"}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-0.5">{m.from_name}</div>
                  {m.text}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={sendChat} className="flex gap-2">
            <input
              data-testid="mp-speed-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={lang === "fr" ? "Votre message…" : "Your message…"}
              className="flex-1 px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
            />
            <button type="submit" data-testid="mp-speed-send" className="px-4 py-2 rounded-lg bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* End screens */}
      {phase === "ended" && (
        <div className="mt-8 rounded-2xl border border-[#E5276A]/30 bg-[#FCE4ED] p-6 text-center" data-testid="mp-end">
          {mode === "battle" && winner && (
            <>
              <Crown className="w-10 h-10 text-[#E5276A] mx-auto" />
              <h2 className="font-display text-2xl mt-2 text-[#1F3A5F]">
                {winner.id === me?.id ? (lang === "fr" ? "Victoire !" : "Victory!") : (lang === "fr" ? `${winner.name} l'emporte` : `${winner.name} wins`)}
              </h2>
              <p className="text-sm text-[#4A5568] mt-1">
                {lang === "fr" ? "Scores" : "Scores"}: {players.map((p) => `${p.name}: ${scores[p.user_id] ?? p.score ?? 0}`).join(" · ")}
              </p>
            </>
          )}
          {mode === "blind" && blindReveal && (
            <>
              <Wine className="w-10 h-10 text-[#E5276A] mx-auto" />
              <h2 className="font-display text-2xl mt-2 text-[#1F3A5F]">{lang === "fr" ? "Réponse" : "Answer"}</h2>
              <p className="text-sm text-[#1F3A5F] mt-2">
                <b>{blindReveal.answer?.color}</b> · <b>{blindReveal.answer?.grape}</b> · <b>{blindReveal.answer?.region}</b>
                {blindReveal.answer?.appellation ? ` · ${blindReveal.answer.appellation}` : ""}{blindReveal.answer?.vintage_approx ? ` · ${blindReveal.answer.vintage_approx}` : ""}
              </p>
              <p className="text-xs text-[#4A5568] mt-3">
                {lang === "fr" ? "Scores" : "Scores"}: {players.map((p) => `${p.name}: ${blindReveal.scores?.[p.user_id] ?? 0}/3`).join(" · ")}
              </p>
            </>
          )}
          {mode === "speed" && (
            <>
              <Users className="w-10 h-10 text-[#E5276A] mx-auto" />
              <h2 className="font-display text-2xl mt-2 text-[#1F3A5F]">{lang === "fr" ? "Session terminée" : "Session ended"}</h2>
              <p className="text-sm text-[#4A5568] mt-1">{lang === "fr" ? "Continuez la conversation par message direct si vous le souhaitez." : "Carry on the conversation in DM if you wish."}</p>
            </>
          )}
          <button onClick={() => navigate("/multiplayer")} className="mt-5 px-5 py-2 rounded-xl bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870]">
            {lang === "fr" ? "Retour au lobby" : "Back to lobby"}
          </button>
        </div>
      )}
    </div>
  );
}
